import { createFileRoute, Link } from "@tanstack/react-router";
import { I18nProvider } from "@/i18n/I18nProvider";
import { Nav } from "@/components/festival/Nav";
import { Footer } from "@/components/festival/Footer";
import { ShieldCheck, Lock, Database, Cookie, UserCheck, Mail } from "lucide-react";

export const Route = createFileRoute("/vertrouwen")({
  head: () => ({
    meta: [
      { title: "Vertrouwen & Beveiliging — Openluchtfilmfestival Apeldoorn" },
      {
        name: "description",
        content:
          "Overzicht van de beveiligings-, privacy- en gegevensmaatregelen van het Openluchtfilmfestival Apeldoorn.",
      },
      { property: "og:title", content: "Vertrouwen & Beveiliging — Openluchtfilmfestival Apeldoorn" },
      {
        property: "og:description",
        content:
          "Hoe wij omgaan met persoonsgegevens, toegang, opslag en cookies bij het Openluchtfilmfestival.",
      },
      { name: "robots", content: "index,follow" },
    ],
  }),
  component: TrustPage,
});

function Card({ icon: Icon, title, children }: { icon: typeof ShieldCheck; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-night-deep/40 p-6">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-gold" />
        <h2 className="font-display text-lg font-medium text-foreground">{title}</h2>
      </div>
      <div className="mt-3 space-y-2 text-sm leading-relaxed text-foreground/80">{children}</div>
    </div>
  );
}

function TrustPage() {
  return (
    <I18nProvider lang="nl">
      <div className="min-h-screen bg-background text-foreground">
        <Nav />
        <main className="mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-24">
          <header className="mb-10">
            <p className="font-mono-cinema text-[11px] uppercase tracking-widest text-gold">Vertrouwen &amp; Beveiliging</p>
            <h1 className="mt-3 font-display text-3xl font-medium text-foreground md:text-5xl">
              Hoe wij omgaan met jouw gegevens
            </h1>
            <p className="mt-4 text-foreground/80">
              Deze pagina wordt onderhouden door het Openluchtfilmfestival Apeldoorn en beschrijft welke
              beveiligings- en privacymaatregelen wij toepassen. Het is een toelichting op onze werkwijze en
              geen onafhankelijke certificering of audit-verklaring.
            </p>
          </header>

          <div className="grid gap-5 md:grid-cols-2">
            <Card icon={ShieldCheck} title="Toegang & authenticatie">
              <p>
                Beheerders loggen in met e-mail/wachtwoord of Google. Rechten worden server-side gecontroleerd
                via een aparte rollen-tabel; rolwijzigingen zijn niet mogelijk vanuit de browser.
              </p>
            </Card>
            <Card icon={Lock} title="Opslag & versleuteling">
              <p>
                Gegevens worden opgeslagen bij onze hostingpartner met versleuteling in transit (HTTPS/TLS) en at
                rest. Sponsor-logo's staan in een private bucket; publieke weergave loopt via beperkte, alleen-lezen
                eindpunten.
              </p>
            </Card>
            <Card icon={Database} title="Gegevensminimalisatie">
              <p>
                Wij verzamelen alleen wat nodig is voor inschrijvingen (vrijwilligers, partneraanvragen, early
                access). Row-Level Security beperkt wie welke records kan inzien; gevoelige velden zijn niet
                publiek opvraagbaar.
              </p>
            </Card>
            <Card icon={Cookie} title="Cookies & tracking">
              <p>
                Wij plaatsen geen niet-noodzakelijke cookies vóór toestemming. Je kunt je voorkeuren altijd
                aanpassen via "Cookie-instellingen wijzigen" in de footer. Zie ook ons{" "}
                <Link to="/privacybeleid" className="text-gold hover:underline">privacybeleid</Link>.
              </p>
            </Card>
            <Card icon={UserCheck} title="Jouw rechten (AVG)">
              <p>
                Je hebt recht op inzage, correctie, verwijdering, beperking, bezwaar en dataportabiliteit.
                Verzoeken behandelen wij binnen de wettelijke termijn. Details staan in het privacybeleid.
              </p>
            </Card>
            <Card icon={Mail} title="Beveiligingsincident melden">
              <p>
                Zie je een kwetsbaarheid of vermoed je een datalek? Mail ons via{" "}
                <a className="text-gold hover:underline" href="mailto:info@openluchtfilmfestival.nl">
                  info@openluchtfilmfestival.nl
                </a>{" "}
                en geef ons redelijke tijd om dit te onderzoeken voordat je het openbaar maakt.
              </p>
            </Card>
          </div>

          <section className="mt-12 rounded-lg border border-border bg-night-deep/40 p-6">
            <h2 className="font-display text-xl font-medium text-foreground">Gedeelde verantwoordelijkheid</h2>
            <p className="mt-3 text-sm text-foreground/80">
              Onze hostingpartner levert het platform en de infrastructuur (rekencapaciteit, opslag, netwerk-
              beveiliging). Het Openluchtfilmfestival is verantwoordelijk voor de inhoud van de app, het
              toegangsbeheer en hoe persoonsgegevens worden verwerkt. Bezoekers zijn verantwoordelijk voor het
              veilig bewaren van hun eigen inloggegevens.
            </p>
          </section>

          <p className="mt-10 text-xs text-foreground/60">
            Laatst bijgewerkt: {new Date().toLocaleDateString("nl-NL", { year: "numeric", month: "long", day: "numeric" })}.
          </p>
        </main>
        <Footer />
      </div>
    </I18nProvider>
  );
}
